export const formatPriceToDataLayer = (price) => {
  const integers = price?.integers.toString().replace('.', '')

  if (!integers || !price?.decimals) {
    return null
  }

  return `${integers}.${price?.decimals}`
}

/**
 * @param  {products} array An array with products items
 * @param  {list} string A string with carousel name
 * @return {object} Data Layer products object
 */

export const setProductImpressionItems = (products = [], list = null) => (
  products.map((product, index) => ({
    id: product.id,
    brand: product.brand,
    category: product.categoryTree,
    name: product.name,
    list: list,
    price: formatPriceToDataLayer(product.price?.to),
    position: index + 1
  }))
)

/**
 * @param  {product} object An object with products details
 * @return {object} Data Layer products object
 */

export const setProductView = ({
  productName,
  productId,
  productBrand,
  toPriceIntegers,
  toPriceDecimals,
  category,
  shopName = null
}) => {
  const price = {
    integers: toPriceIntegers,
    decimals: toPriceDecimals
  }

  return ({
    ecommerce: {
      detail: {
        products: [{
          name: productName,
          id: productId,
          price: formatPriceToDataLayer(price),
          brand: productBrand,
          category: category,
          dimension15: shopName
        }]
      }
    }
  })
}

/**
 * @param  {product} object An object with products details
 * @return {object} Data Layer products object
 */

export const addToCartDataLayer = ({
  regionId,
  productName,
  productId,
  price,
  productBrand,
  category,
  dimension8,
  dimension9,
  dimension15,
  quantity = 1
}) => ({
  event: 'addToCart',
  dimension14: regionId,
  ecommerce: {
    currencyCode: 'BRL',
    add: {
      products: [{
        name: productName,
        id: productId,
        price,
        brand: productBrand,
        category: category,
        quantity,
        dimension8,
        dimension9,
        dimension15
      }]
    }
  }
})

/**
 * @param  {product} object An object with products details
 * @return {object} Data Layer products object
 */

export const removeProductDataLayer = ({
  brand,
  categoryTree,
  productId,
  name,
  price: { to },
  quantity,
  regionId
}) => ({
  event: 'removeFromCart',
  dimension14: regionId,
  ecommerce: {
    remove: {
      products: [{
        name,
        id: productId,
        price: to,
        brand,
        category: categoryTree || null,
        quantity
      }]
    }
  }
})

export const addServiceToCartDataLayer = ({
  regionId,
  serviceId,
  serviceName,
  price: { to },
  category = null,
  quantity = 1,
  productName
}) => ({
  event: 'addToCart',
  dimension14: regionId,
  ecommerce: {
    currencyCode: 'BRL',
    add: {
      products: [{
        name: serviceName,
        id: serviceId,
        price: to,
        brand: 'Serviço',
        category,
        quantity,
        dimension8: 'delivery',
        dimension9: 'Leroy Merlin',
        dimension15: 'Leroy Merlin',
        dimension31: productName ?? ''
      }]
    }
  }
})

export const removeServiceFromCartDataLayer = ({
  regionId,
  serviceId,
  serviceName,
  price: { to },
  category = null,
  quantity
}) => ({
  event: 'removeFromCart',
  dimension14: regionId,
  ecommerce: {
    remove: {
      products: [{
        name: serviceName,
        id: serviceId,
        price: to,
        brand: 'Serviço',
        category,
        quantity
      }]
    }
  }
})

/**
 * @param  {product} object An object with products details
 * @return {object} Data Layer products object
 */

export const cartCepToDataLayer = products => ({
  event: 'cep-carrinho',
  products
})

/**
 * @param  {product} object An object with products details
 * @return {object} Data Layer products object
 */

export const checkoutCartPurchaseToDataLayer = ({
  regionId,
  products
}) => ({
  event: 'checkout',
  dimension14: regionId,
  ecommerce: {
    checkout: {
      actionField: { step: 1, action: 'checkout' },
      products
    }
  }
})

/**
 * @param  {user} object An object with user login information
 */

export const dispatchLoginToDataLayer = ({ user }) => ({
  event: 'login',
  userId: user?.id,
  dimension24: user?.favoriteStore?.slug
})

/**
 * @param  {productObj} object An object with product clicked information
 */

export const onProductClick = (productObj, { list, position }) => {
  const { categoryTree, category } = productObj

  const dataObject = {
    event: 'productClick',
    ecommerce: {
      click: {
        actionField: { list: list },
        products: [{
          name: productObj.name,
          id: productObj.id,
          price: formatPriceToDataLayer(productObj?.price?.to),
          brand: productObj.brand,
          ...(categoryTree ? { category: productObj.categoryTree } : category),
          position: position
        }]
      }
    }
  }

  pushDataObject(dataObject)
}

/**
 * @param  {menuItem} object An object with clicked menu's item information
 */

export const onMenuClick = (menuItem) => {
  const { rootElement, parent, item, isMobile } = menuItem

  const menuTreeArray = [rootElement, parent, item.name]
  const filteredMenuTreeArray = menuTreeArray.filter(Boolean)

  const menuName = filteredMenuTreeArray.join(' - ')

  const element = isMobile ? 'data-mobile-menu' : 'data-desktop-menu'

  const dataObject = {
    event: 'menuInteraction',
    element,
    menuName,
    menuId: item.id,
    originMenuName: rootElement || item.name
  }

  pushDataObject(dataObject)
}

/**
 * @param  {carouselItem} object An object with clicked carousel's item information
 */

export const onCarouselItemClick = (carouselItem) => {
  const { name, carouselTitle, contentName, id } = carouselItem

  const dataObject = {
    event: 'carouselInteraction',
    element: carouselTitle,
    iconsCarouselName: `${contentName} - ${name}`,
    departmentId: id
  }

  pushDataObject(dataObject)
}

/**
 * @param  {crossProduct} object An object with clicked cross' item information
 */

export const onCrossItemClick = (crossProduct) => {
  const {
    crossTitle,
    productName,
    productId,
    page,
    crossIndex = 0,
    source,
    productIndex,
    productsIds = [],
    originProductId = null
  } = crossProduct

  const normalizeIndex = index => index + 1

  const dataObject = {
    event: 'crossSellingClick',
    crossSellingList: crossTitle,
    crossSellingProduct: productName,
    crossSellingId: productId,
    crossOriginProductId: originProductId,
    crossSellingPage: page,
    crossSource: source,
    crossPosition: normalizeIndex(crossIndex),
    crossProductList: productsIds.join(','),
    crossSellingProductPosition: normalizeIndex(productIndex)
  }

  pushDataObject(dataObject)
}

/**
 * @param  {dataObject} object An object with dataLayer information
 */

export const pushDataObject = (dataObject) => {
  window.dataLayer = window.dataLayer || []

  window.dataLayer.push(dataObject)
}
